import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { ThemePalette } from '@angular/material/core';
import { LocalNumberPipe } from '@app/shared/pipes/local-number.pipe';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { filter, flatMap, isEmpty, isNil, orderBy } from 'lodash';
import moment from 'moment';
import { forkJoin, from, Observable, of, Subject } from 'rxjs';
import { catchError, filter as rxjsFilter, map, mergeMap, takeUntil, toArray } from 'rxjs/operators';
import { BuildingEfficiencyWizardService } from '../building-efficiency-wizard.component.service';
import { BuildingEfficiencyWizardStore } from '../building-efficiency-wizard.component.store';
import { HeatSelectionService } from './heat-selection.service';
import { IHeatData, MeterHeatData } from '@app/efficiency-analysis/models/heat-data';
import { IRawHeatData } from '@app/efficiency-analysis/models/raw-heat-data';
import { IRawWizardData } from '@app/efficiency-analysis/models/raw-wizard-data';
import { Task } from '@app/efficiency-analysis/models/heat-task';
import { MeterView, MeterViewResponse } from '@app/shared/models/meter-data/meter-view';
import { EfficiencyAnalysisMeters } from '@app/shared/models/efficiency-analysis/efficiency-analysis-meters';
import { DateUtilities } from '@app/shared/date-utility';
import { DotDayMonthYearFormat } from '@app/shared/constants';

@Component({
  selector: 'app-heat-selection',
  templateUrl: './heat-selection.component.html',
  styleUrls: ['./heat-selection.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class HeatSelectionComponent implements OnInit, OnDestroy {
  protected ngUnsubscribe: Subject<void> = new Subject<void>();
  @Input() savedEAReferenceData: any;
  @Output() nextButtonStatus = new EventEmitter<boolean>();

  private _rawWizardData: IRawWizardData = {} as IRawWizardData;
  private _dsoProductIds: any;
  private _metersConsumption: MeterView[] = [];

  public displayStartDate = '';
  public displayEndDate = '';
  public color: ThemePalette = 'primary';
  public note = this.translate.instant('module.shared.buildingEfficiencyWizard.step2.noteText');

  cubicMeterToKwh = 10;
  public showTask = false;
  public sum = 0;
  public toggled = true;
  public showToolTip = true;
  public hasConsumptionOrHistorical: boolean = false;
  //Don't try to assign this to a constant
  public heatSelectionTasks: Task[] = [
    {
      allComplete: false,
      productId: '',
      completed: false,
      name: 'Strom',
      displayName: 'Heizstrom',
      consumptionLabel: 'kWh',
      calorificValueHi: 1,
      tooltipMessage: '',
      subtasks: [],
      meterNumber: null,
    },
    {
      allComplete: false,
      productId: '',
      completed: false,
      name: 'Gas',
      displayName: 'Gas',
      consumptionLabel: 'kWh',
      // consumptionLabel: 'm³', keeping in case meter cube would be reimplemented again
      calorificValueHi: 0.9,
      tooltipMessage: '',
      subtasks: [],
      meterNumber: null,
    },
    {
      allComplete: false,
      productId: '',
      completed: false,
      name: 'Heizöl',
      displayName: 'Heizöl',
      consumptionLabel: 'Liter',
      calorificValueHi: 10,
      tooltipMessage: '',
      subtasks: [],
      meterNumber: null,
    },
    {
      allComplete: false,
      productId: '',
      completed: false,
      name: 'Flüssiggas',
      displayName: 'Flüssiggas',
      consumptionLabel: 'kg',
      calorificValueHi: 13,
      tooltipMessage: '',
      subtasks: [],
      meterNumber: null,
    },
    {
      allComplete: false,
      productId: '',
      completed: false,
      name: 'Fernwärme',
      displayName: 'Fernwärme',
      consumptionLabel: 'kWh',
      calorificValueHi: 1,
      tooltipMessage: '',
      subtasks: [],
      meterNumber: null,
    },
    {
      allComplete: false,
      productId: '',
      completed: false,
      name: 'Pellet/Holz',
      displayName: 'Pellet/Holz',
      consumptionLabel: 'kg',
      calorificValueHi: 4.9,
      tooltipMessage: '',
      subtasks: [],
      meterNumber: null,
    },
  ];
  public temptask = this.heatSelectionTasks;

  constructor(
    public localNumberPipe: LocalNumberPipe,
    public translate: TranslateService,
    private _buildingEfficiencyWizardStore: BuildingEfficiencyWizardStore,
    private _overlayLoaderService: OverlayLoaderService,
    private buildingEfficiencyWizardService: BuildingEfficiencyWizardService,
    private heatSelectionService: HeatSelectionService
  ) {}

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public ngOnInit(): void {
    this._dsoProductIds = environment.consumptionWizardHeatProductIds;
    this.heatSelectionService.toggledAction$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((isToggled: boolean) => (this.toggled = isToggled));
    this._listenToEAWizardDataChanges();
  }

  private _listenToEAWizardDataChanges(): void {
    this._buildingEfficiencyWizardStore.wizardData$
      .pipe(
        rxjsFilter((wizard: IRawWizardData | null): wizard is IRawWizardData => !isNil(wizard)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((_rawWizardData: IRawWizardData) => {
        this._rawWizardData = _rawWizardData;

        this._setStartEndDateDisplay(_rawWizardData);
        if (isNil(this._buildingEfficiencyWizardStore.heatSelections) || isEmpty(this._buildingEfficiencyWizardStore.heatSelections)) {
          this._initGetMeterConsumption(_rawWizardData.meterList);
        } else {
          this.heatSelectionTasks = this._buildingEfficiencyWizardStore.heatSelections;
          this._sumConsumption();
          this._checkMeterHasHistoryAndData();
        }
      });
  }

  private _setStartEndDateDisplay(rawWizardData: IRawWizardData): void {
    const hasTimePeriod = rawWizardData.hasTimePeriod;
    const userDefinedStartDate = String(rawWizardData.userDefinedStartDate);
    const defaultStartDate = rawWizardData.defaultStartDate;
    const userDefinedEndDate = String(rawWizardData.userDefinedEndDate);
    const defaultEndDate = rawWizardData.defaultEndDate;

    const displayStartDate = hasTimePeriod ? userDefinedStartDate : defaultStartDate;

    this.displayStartDate = this.getDisplayDate(displayStartDate);

    const displayEndDate = hasTimePeriod ? userDefinedEndDate : defaultEndDate;

    this.displayEndDate = this.getDisplayDate(displayEndDate);
  }

  private _resetInputsWithoutMeters(): void {
    this.heatSelectionTasks.forEach((e: Task) => {
      if (e.subtasks) {
        if (e.subtasks[0].completed && isNil(e.subtasks[0].meterNumber)) {
          e.allComplete = false;
        }
        e.subtasks.forEach((s: Task) => {
          if (isNil(s.meterNumber) && !isNil(s.totalMeterConsumption)) {
            s.value = undefined;
            s.toSumValue = undefined;
            s.totalMeterConsumption = undefined;
            s.completed = false;
          }
        });
      }
    });
  }

  private _initGetMeterConsumption(meters: MeterView[]): void {
    const hasMeters = meters.length > 0;
    if (hasMeters) {
      this._toggleLoader(true);
      this._getMetersConsumption(meters)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: (consumptions: MeterView[]) => this._initAssignMeterConsumptionToHeatType(consumptions),
          error: () => (this._metersConsumption = meters),
          complete: () => this._initAssignMeterToHeatTypeDisplay(),
        });
    } else {
      this._metersConsumption = meters;
      this._initAssignMeterToHeatTypeDisplay();
    }
  }

  private _getMetersConsumption(meters: MeterView[]): Observable<MeterView[]> {
    let startDateToMoment = new Date();
    let endDateToMoment = new Date();

    if (this._rawWizardData) {
      const hasTimePeriod = this._rawWizardData.hasTimePeriod;
      const userDefinedStartDate = this._rawWizardData.userDefinedStartDate;
      const defaultStartDate = this._rawWizardData.defaultStartDate;
      const userDefinedEndDate = this._rawWizardData.userDefinedEndDate;
      const defaultEndDate = this._rawWizardData.defaultEndDate;
      const startDate = hasTimePeriod ? userDefinedStartDate : defaultStartDate;
      startDateToMoment = this._transformDateToMoment(startDate);
      const endDate = hasTimePeriod ? userDefinedEndDate : defaultEndDate;
      endDateToMoment = this._transformDateToMoment(endDate);
    }

    return from(meters).pipe(
      mergeMap((meter: MeterView) => this._getMeterConsumption(meter, startDateToMoment, endDateToMoment)),
      toArray(),
      catchError(() => of(meters))
    );
  }

  private _getMeterConsumption(meter: MeterView, startDate: Date, endDate: Date): Observable<MeterView> {
    return this.buildingEfficiencyWizardService.getConsumption(meter, startDate, endDate).pipe(
      map((consumptions: number) => {
        return {
          ...meter,
          hasMeterConsumption: true,
          tooltipMessage: this.translate.instant('module.shared.buildingEfficiencyWizard.step3.hasConsumptionTooltipText'),
          totalMeterConsumption: consumptions,
        } as MeterView;
      }),
      catchError(() =>
        of({
          ...meter,
          hasMeterConsumption: false,
          tooltipMessage: this.translate.instant('module.shared.buildingEfficiencyWizard.step3.hasNoConsumptionAndHistoricalTooltipText'),
          totalMeterConsumption: undefined,
        } as MeterView)
      )
    );
  }

  private _initAssignMeterConsumptionToHeatType(metersConsumption: MeterView[]): void {
    const meterSubscriptions: Observable<MeterViewResponse>[] = [];
    this._toggleLoader(true);

    metersConsumption.forEach((meterConsumption: MeterView) => {
      this._metersConsumption.push(meterConsumption);
      meterSubscriptions.push(this.buildingEfficiencyWizardService.getMetersByMelo(meterConsumption.measurementLocationKey, meterConsumption.municipalityKey));
    });

    const meterConsumptionSubscriptions: Observable<MeterView[]>[] = [];
    forkJoin(meterSubscriptions)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (metersView: MeterViewResponse[]) => {
          this._toggleLoader(true);
          metersView.forEach((meterView: MeterViewResponse) => {
            meterConsumptionSubscriptions.push(this._getMetersConsumption(meterView.data));
          });

          forkJoin(meterConsumptionSubscriptions)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe({
              next: (metersConsumptionResult: MeterView[][]) => {
                this._toggleLoader(true);
                metersConsumptionResult.forEach((meterConsumptionResult: MeterView[]) => {
                  const sortMeterByDate = orderBy(meterConsumptionResult, (s: MeterView) => new Date(s.usageStartDate), 'desc');

                  const filterMeterConsumption: MeterView[] = filter(sortMeterByDate, (value: MeterView) => !isNil(value.totalMeterConsumption));

                  const hasMeterConsumption = filterMeterConsumption.length > 0;

                  if (hasMeterConsumption) {
                    this._metersConsumption.push(filterMeterConsumption[0]);
                  } else {
                    this._metersConsumption.push(sortMeterByDate[0]);
                  }
                });
              },
              error: () => this._toggleLoader(false),
              complete: () => {
                this._initAssignMeterToHeatTypeDisplay();
                this._onComplete();
              },
            });
        },
        error: () => this._toggleLoader(false),
        complete: () => {
          this._initAssignMeterToHeatTypeDisplay();
          if (meterConsumptionSubscriptions.length === 0) {
            this._onComplete();
          }
        },
      });
  }

  private _initAssignMeterToHeatTypeDisplay(): void {
    this.heatSelectionTasks = this.temptask;

    this.heatSelectionTasks.forEach((task: Task) => {
      const consumptions = this.getMeterConsumptionForTask(task);

      consumptions?.forEach((meter: MeterView) => this.assignMeterConsumptionToSubTask(meter, task));

      const efficiencyMeter =
        this.heatSelectionService.efficiencyMeters.find(
          (meter: EfficiencyAnalysisMeters) =>
            meter.energyTypeName.toLowerCase() === task.name.toLowerCase() && (isNil(meter.meterNumber) || isEmpty(meter.meterNumber))
        ) ?? ({} as EfficiencyAnalysisMeters);

      if (task.subtasks.length === 0) {
        task.subtasks.push({
          guid: efficiencyMeter.guid,
          productId: '',
          name: '',
          completed: !this.buildingEfficiencyWizardService.isPeriodChanged ? efficiencyMeter.isSelected : !isNil(efficiencyMeter.value),
          hasMeterConsumption: false,
          meterNumber: null,
          totalMeterConsumption: efficiencyMeter.value,
          value: this.localNumberPipe.transform(efficiencyMeter.value),
          marketLocationKey: '',
          tooltipMessage: '',
          calorificValueHi: task.calorificValueHi,
          toSumValue: !isNil(efficiencyMeter.value) && !isNil(task.calorificValueHi) ? (efficiencyMeter.value as number) * task.calorificValueHi : undefined,
        } as Task);

        task.allComplete = this.buildingEfficiencyWizardService.isPeriodChanged ? !isNil(efficiencyMeter.value) : !!efficiencyMeter.isSelected;
      }
    });

    this.heatSelectionTasks = this.heatSelectionTasks.map((task: Task) => {
      if (!isNil(task.subtasks) && !isEmpty(task.subtasks) && !isNil(task.subtasks[0].meterNumber)) {
        task.subtasks = task.subtasks.filter((sub: Task) => !isNil(sub.meterNumber));
        task.allComplete = task.subtasks.every((sub: Task) => sub.completed);
      }
      return task;
    });

    if (this.buildingEfficiencyWizardService.isPeriodChanged) {
      this._resetInputsWithoutMeters();
    }

    this._sumConsumption();
    this._checkMeterHasHistoryAndData();
  }

  private isProductIdFound = (meter: any, productIds = this._dsoProductIds) => !isNil(this._dsoProductIds.find((x: any) => meter.productId === x));

  private getMeterConsumptionForTask(task: Task, meterConsumptions = this._metersConsumption) {
    if (task.name.toLowerCase() === 'strom') {
      return meterConsumptions.filter(
        (meterConsumption) =>
          !isNil(meterConsumption) && meterConsumption.energyTypesName.toLowerCase() === task.name.toLowerCase() && this.isProductIdFound(meterConsumption)
      );
    }
    if (task.name.toLowerCase() === 'gas') {
      return meterConsumptions.filter(
        (meterConsumption) => !isNil(meterConsumption) && meterConsumption.energyTypesName.toLowerCase() === task.name.toLowerCase()
      );
    }
  }

  private findExistingMeterConsumptionInTask(meterConsumption: MeterView, task: Task): Task | undefined {
    return task.subtasks.find((task: Task) => task.meterNumber === meterConsumption.meterNumber);
  }

  private assignMeterConsumptionToSubTask(meterConsumption: MeterView, task: Task): Task {
    const efficiencyMeter =
      orderBy(this.heatSelectionService.efficiencyMeters, ['creationDate'], 'desc').find(
        (eaMeters: EfficiencyAnalysisMeters) =>
          eaMeters.energyTypeName.toLowerCase() === task.name.toLowerCase() && eaMeters.meterNumber === meterConsumption.meterNumber
      ) ?? ({} as EfficiencyAnalysisMeters);
    const newTask = this.findExistingMeterConsumptionInTask(meterConsumption, task) ?? ({} as Task);

    const startDate = this._rawWizardData?.hasTimePeriod ? this._rawWizardData.userDefinedStartDate : this._rawWizardData?.defaultStartDate;

    let useAutoSuggestedConsumption = false;
    if (
      (!isEmpty(newTask) &&
        this.buildingEfficiencyWizardService.isPeriodChanged &&
        !isNil(this.savedEAReferenceData.efficiencyAnalysisDate) &&
        this.savedEAReferenceData.isHeatValueSuggested &&
        (startDate !== this.savedEAReferenceData.efficiencyAnalysisStartDate ||
          (startDate === this.savedEAReferenceData.efficiencyAnalysisStartDate && this.savedEAReferenceData.isHeatValueSuggested))) ||
      (isEmpty(this.savedEAReferenceData) && meterConsumption.hasMeterConsumption)
    ) {
      useAutoSuggestedConsumption = true;
    }

    const isComplete = efficiencyMeter.value;

    Object.assign(newTask, {
      guid: efficiencyMeter.guid,
      productId: meterConsumption.productId,
      name: this._getObjectTypeName(meterConsumption),
      completed: useAutoSuggestedConsumption ? meterConsumption.hasMeterConsumption : isComplete,
      hasMeterConsumption: meterConsumption.hasMeterConsumption,
      meterNumber: meterConsumption.meterNumber,
      totalMeterConsumption: useAutoSuggestedConsumption ? meterConsumption.totalMeterConsumption : efficiencyMeter.value,
      value: useAutoSuggestedConsumption ? meterConsumption.totalMeterConsumption : this.localNumberPipe.transform(efficiencyMeter.value),
      toSumValue: useAutoSuggestedConsumption
        ? (meterConsumption.totalMeterConsumption as number) * (task.calorificValueHi as number)
        : (efficiencyMeter.value as number) * (task.calorificValueHi as number),
      autoSuggestedConsumption: meterConsumption.totalMeterConsumption,
      marketLocationKey: meterConsumption.marketLocationKey,
      tooltipMessage: meterConsumption.tooltipMessage,
      calorificValueHi: task.calorificValueHi,
    } as Task);

    if (!this.findExistingMeterConsumptionInTask(meterConsumption, task)) {
      task.subtasks.push(newTask);
    }

    task.subtasks.sort((a, b) => a.name.localeCompare(b.name));

    this.toggled = isNil(efficiencyMeter.value);

    if (
      startDate !== this.savedEAReferenceData.efficiencyAnalysisStartDate ||
      (startDate === this.savedEAReferenceData.efficiencyAnalysisStartDate &&
        (this.savedEAReferenceData.isHeatValueSuggested || useAutoSuggestedConsumption || this.buildingEfficiencyWizardService.isPeriodChanged))
    ) {
      this.toggled = true;
      this.onToggleChange(this.toggled);
    }

    this.heatSelectionService.toggledSubject$.next(this.toggled);

    return task;
  }

  private _getObjectTypeName(data: MeterView): string {
    if (data.objectType) {
      return data.objectType;
    } else if (data.objectTypeObj) {
      return data.objectTypeObj.name;
    } else {
      return '';
    }
  }

  private _checkMeterHasHistoryAndData(): void {
    const subtasks = flatMap(this.heatSelectionTasks, (v) => v.subtasks);
    const subtasksHasProductId: any = filter(subtasks, (v: Task) => v.productId !== null);
    if (subtasksHasProductId.length > 0 && subtasksHasProductId[0].totalMeterConsumption > 0) {
      this.hasConsumptionOrHistorical = true;
    } else {
      this.hasConsumptionOrHistorical = false;
    }
    this._sumConsumption();
  }

  private _onComplete(): void {
    this._toggleLoader(false);
    this._setNextButtonStatus();
  }

  private _toggleLoader(show: boolean): void {
    this._overlayLoaderService.isLoading = show;
  }

  private _transformDateToMoment(date: string): Date {
    return moment(date).toDate();
  }

  public updateAllComplete(index: number): void {
    this.heatSelectionTasks[index].allComplete =
      !isNil(this.heatSelectionTasks[index].subtasks) && this.heatSelectionTasks[index].subtasks.every((sub: Task) => sub.completed);
    this._sumConsumption();
  }

  private _sumConsumption(): void {
    this.sum = 0;

    this.heatSelectionTasks.forEach((t) => {
      t.subtasks.forEach((s) => {
        if (s.completed && s.toSumValue !== undefined && !isNaN(s.toSumValue)) {
          this.sum += s.toSumValue;
        }
      });
    });
    this._updateHeatData();
    this._setNextButtonStatus();
  }

  private _updateHeatData(): void {
    const heatData: IHeatData[] = [];

    this.heatSelectionTasks.forEach((e: Task) => {
      let heat: IHeatData;
      if (e.subtasks && e.subtasks.length > 0) {
        const startDate = this._rawWizardData.hasTimePeriod ? this._rawWizardData.userDefinedStartDate : this._rawWizardData.defaultStartDate;
        heat = {
          consumptionLabel: e.consumptionLabel,
          energyTypeName: e.name,
          calorificValueHi: e.calorificValueHi,
          floorArea: this._rawWizardData?.buildingEfficiencyData.floorArea,
          startDate: new Date(startDate),
          checked: e.completed,
          totalConsumption: 0,
          meters: [],
        };
        e.subtasks.forEach((subtask: Task) => {
          const newHeat: MeterHeatData = {
            meterNumber: subtask.meterNumber,
            totalConsumption: subtask.completed && !isNil(subtask.toSumValue) ? subtask.toSumValue : 0,
          };

          if (subtask.completed) {
            //TODO: RECONSIDER DATA TYPE RE:B2M
            heat.totalConsumption += subtask.toSumValue ?? 0;
          }

          heat.meters?.push(newHeat);
        });
      } else {
        const startDate = this._rawWizardData.hasTimePeriod ? this._rawWizardData.userDefinedStartDate : this._rawWizardData.defaultStartDate;

        heat = {
          consumptionLabel: e.consumptionLabel,
          energyTypeName: e.name,
          totalConsumption: e.value as number,
          calorificValueHi: e.calorificValueHi,
          floorArea: this._rawWizardData?.buildingEfficiencyData.floorArea,
          startDate: new Date(startDate),
          checked: e.completed,
          meters: [],
        };
      }
      heatData.push(heat);
    });

    const newHeatData: IRawHeatData = {
      totalConsumption: this.sum,
      heatData: heatData,
    };

    this._buildingEfficiencyWizardStore.updateHeat(newHeatData);
    this._buildingEfficiencyWizardStore.heatSelections = this.heatSelectionTasks;
  }

  public updateValue(taskIndex: number, subTaskIndex: number, value: string): void {
    const parse = parseInt(value, 10);
    if (
      !isEmpty(this.heatSelectionTasks) &&
      !isEmpty(this.heatSelectionTasks[taskIndex]) &&
      !isEmpty(this.heatSelectionTasks[taskIndex].subtasks) &&
      !isEmpty(this.heatSelectionTasks[taskIndex].subtasks[subTaskIndex])
    ) {
      if (!isNil(this.heatSelectionTasks[taskIndex].subtasks[subTaskIndex].meterNumber)) {
        this.toggled = false;
        this.heatSelectionService.toggledSubject$.next(this.toggled);
      }
      this.heatSelectionTasks[taskIndex].subtasks[subTaskIndex].value = parse;
      this.heatSelectionTasks[taskIndex].subtasks[subTaskIndex].toSumValue = parse
        ? parse * (this.heatSelectionTasks[taskIndex].subtasks[subTaskIndex].calorificValueHi as number)
        : undefined;

      if (parse) {
        this.heatSelectionTasks[taskIndex].subtasks[subTaskIndex].completed = true;
      }
    }

    this.updateAllComplete(taskIndex);
    this._sumConsumption();
    this._buildingEfficiencyWizardStore.setIsComputedValuesChanged(true);
  }

  public focus(taskIndex: number, subTaskIndex: number, value: string): void {
    const parse = parseInt(value, 10);
    const germanNumberFormat = value.replace(',0', '').split(',').join('').split('.').join('');

    if (!isNil(parse)) {
      if (this.heatSelectionTasks[taskIndex].subtasks[subTaskIndex]) {
        this.heatSelectionTasks[taskIndex].subtasks[subTaskIndex].value = parse ? germanNumberFormat : undefined;
      }
    }
  }

  public blur(taskIndex: number, subTaskIndex: number, value: any): void {
    const parse = parseInt(value, 10);
    const convert = this.localNumberPipe.transform(parseInt(value, 10));

    if (parse) {
      this.heatSelectionTasks[taskIndex].subtasks[subTaskIndex].value = convert;
    }
  }

  public numberOnly(event: any): boolean | null {
    const prevValue = event.target.value;
    const allowInput = event.charCode === 8 || event.charCode === 0 ? null : event.charCode >= 48 && event.charCode <= 57;

    const valueLength = prevValue.length + (allowInput ? 1 : 0);

    if (valueLength > 8) {
      return false;
    }

    return allowInput;
  }

  public someComplete(index: number): boolean | null {
    if (this.heatSelectionTasks[index].subtasks.length === 1) {
      if (this.heatSelectionTasks[index].subtasks.filter((t) => t.completed).length === 1) {
        return null;
      } else {
        return false;
      }
    }
    const result =
      this.heatSelectionTasks[index].subtasks.filter((t) => t.completed).length === this.heatSelectionTasks[index].subtasks.length &&
      this.heatSelectionTasks[index].allComplete;

    return !result;
  }

  public setAll(index: number, completed: boolean): void {
    this.heatSelectionTasks[index].allComplete = completed;
    if (this.heatSelectionTasks[index].subtasks == null) {
      return;
    }
    this.heatSelectionTasks[index].subtasks.forEach((t) => (t.completed = completed));
    this._sumConsumption();
    this._buildingEfficiencyWizardStore.setIsComputedValuesChanged(true);
  }

  public onToggleChange(toggled: boolean): void {
    if (toggled) {
      this.heatSelectionTasks.forEach((e) => {
        const startDate = this._rawWizardData?.hasTimePeriod ? this._rawWizardData.userDefinedStartDate : this._rawWizardData?.defaultStartDate;
        let isPeriodChanged = false;
        if (startDate === this.savedEAReferenceData.efficiencyAnalysisStartDate && this.buildingEfficiencyWizardService.isPeriodChanged) {
          isPeriodChanged = true;
        }

        e.subtasks.forEach((s) => {
          if (!isNil(s.meterNumber)) {
            const consumption =
              !isNil(s.autoSuggestedConsumption) || isPeriodChanged || !s.hasMeterConsumption ? s.autoSuggestedConsumption : s.totalMeterConsumption;
            s.value = this.localNumberPipe.transform(consumption);
            s.toSumValue = isNil(consumption) || isNil(s.calorificValueHi) ? undefined : consumption * s.calorificValueHi;
            if (!s.hasMeterConsumption && isNil(consumption)) {
              s.completed = false;
            }
            if (isPeriodChanged) {
              s.completed = !isNil(consumption);
            }
          }
        });
      });
    } else {
      this.heatSelectionTasks.forEach((e) => {
        e.subtasks.forEach((s) => {
          if (!isNil(s.meterNumber)) {
            s.value = undefined;
            s.toSumValue = undefined;
          }
        });
      });
    }
    this._sumConsumption();
    this.heatSelectionService.toggledSubject$.next(toggled);
  }

  private _setNextButtonStatus(): void {
    let hasEmptyInput = false;
    this.heatSelectionTasks.forEach((t) => {
      t.subtasks.forEach((s) => {
        if (s.completed && (s.toSumValue === undefined || isNil(s.toSumValue) || isNaN(s.toSumValue))) {
          hasEmptyInput = true;
        }
      });
    });

    if (this.sum > 0 && !hasEmptyInput) {
      this._updateHeatData();
      this.nextButtonStatus.emit(false);
    } else {
      this.nextButtonStatus.emit(true);
    }
  }

  getToggleVisibility() {
    let hasAutoSuggestedConsumption = false;
    this.heatSelectionTasks.forEach((e) => {
      e.subtasks.forEach((s) => {
        if (!isNil(s.autoSuggestedConsumption)) {
          hasAutoSuggestedConsumption = true;
        }
      });
    });

    return hasAutoSuggestedConsumption;
  }

  private getDisplayDate(date: string | Date): string {
    return DateUtilities.toExpectedFormat(date, DotDayMonthYearFormat);
  }
}
